import React, { useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { FilterIcon, PlusIcon, ChevronDoubleRightIcon } from '@vlabs/icons';
import {
  Page, PageContent, PageHeader, usePageContext,
} from '@vlabs/monokit/shared/ui/page';
import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import { useDebouncedSetFilters } from '@vlabs/shared/components/filters-panel/hooks';
import { permissions } from '@vlabs/shared/config';
import { routes } from '@vlabs/shared/navigation/routes';
import {
  PageWithSidebar, Divider, Control, Tab, Tabs, useModal, Badge,
} from '@vlabs/uikit';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { viewerCan } from '@vlabs/pages/auth/selectors';
import { selectAnalyticData } from '@vlabs/pages/video-analytics/selectors';

import { updateFilters } from './store';
import FilterForm from './streams/components/filters/FilterForm';
import { SelectVideoAnalyticsModal } from './streams/components/select-va-modal/SelectVideoAnalyticsModal';
import StreamPage from './streams/StreamsPage';
import st from './VideoAnalytics.module.sass';

function VideoAnatylicsPage({
  can, filters, setFilters, filterCounts,
}) {
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const selectAnalyticsModal = useModal({ size: 'small', className: st.Modal });
  const history = useHistory();
  const { t } = useTranslation();
  const formMethods = useForm({ defaultValues: filters });
  const { getTitleComponent } = usePageContext();

  const { watch, trigger } = formMethods;
  useDebouncedSetFilters({ trigger, watch }, setFilters);

  const onSubmit = async (analytics) => {
    const { stream_id } = await apiContainer.lunaClient.streams.create({ payload: analytics });
    if (stream_id) {
      const pathToEditStream = generatePath(routes.videoAnalytics.streams.edit, { streamId: stream_id });
      history.push(pathToEditStream);
    }
  };

  const actions = (
    <React.Fragment>
      {can([permissions.videoanalytics.creation]) && (
      <Control.Button
        data-testid="addStreamsButton"
        icon={<PlusIcon />}
        onClick={selectAnalyticsModal.open}
        type="button"
      >
        {t('shared:btn.add')}
      </Control.Button>
      )}
      <Divider small />
      <Control.Button
        data-testid="checkVideoFilesButton"
        disabled
        kind="secondary"
        type="button"
        // onClick={}
        variant="filled"
      >
        {t('videoAnalytics:buttons.check_videofiles')}
      </Control.Button>
      <Divider small />
      <Control.Button
        data-testid="addFiltersButton"
        icon={isFiltersVisible ? <ChevronDoubleRightIcon /> : <FilterIcon />}
        kind={isFiltersVisible ? 'secondary' : 'primary'}
        onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        type="button"
        variant={isFiltersVisible ? 'filled' : 'outlined'}
      >
        {t('videoAnalytics:buttons.filter')}
        {filterCounts > 0 && (
        <Badge
          className={st.FiltersBadge}
          value={filterCounts}
        />
        )}
      </Control.Button>
    </React.Fragment>
  );

  const tabs = (
    <Tabs>
      <Tab
        data-testid="videoanalyticsStreams"
        header={t('videoAnalytics:tabs.streams')}
        name="videoanalyticsStreams"
      />
      <Tab
        data-testid="videoanalyticsGroups"
        disabled
        header={t('videoAnalytics:tabs.groups')}
        name="videoanalyticsGroups"
      />
      <Tab
        data-testid="videoanalyticsLogs"
        disabled
        header={t('videoAnalytics:tabs.logs')}
        name="videoanalyticsLogs"
      />
    </Tabs>
  );

  const context = { ...formMethods, setFilters };

  return (
    <FormProvider {...context}>
      <form style={{ flex: 1 }}>
        <PageWithSidebar
          className="FullHeightSidebar"
          isOpenRightSidebar={isFiltersVisible}
          rightSidebar={(
            <React.Fragment>
              <FiltersHeader
                isAdaptive
                onClick={() => setIsFiltersVisible((prev) => !prev)}
              />
              <FilterForm />
            </React.Fragment>
            )}
        >
          {selectAnalyticsModal.wrap(
            <SelectVideoAnalyticsModal
              closeModal={selectAnalyticsModal.close}
              onSubmit={(selectedAnalytics) => onSubmit(selectedAnalytics)}
            />,
          )}
          <Page>
            <PageContent>
              <PageHeader
                actions={actions}
                title={(
                  <React.Fragment>
                    {getTitleComponent(t('videoAnalytics:table_header.videoanalytics'))}
                    {tabs}
                  </React.Fragment>
                )}
              />
              <StreamPage />
            </PageContent>
          </Page>
        </PageWithSidebar>
      </form>
    </FormProvider>
  );
}

export default connect(
  (state) => ({
    can: viewerCan(state),
    filters: selectAnalyticData(state, 'filters'),
    filterCounts: selectAnalyticData(state, 'filterCounts'),
  }),
  (dispatch) => ({
    setFilters: (filters) => dispatch(updateFilters({ filters })),
  }),
)(VideoAnatylicsPage);
